import { QueryClient } from '@tanstack/react-query';

/**
 * Cache time constants (in milliseconds)
 * Used for configuring query gcTime based on data criticality.
 * Offline support is handled by IndexedDB persister, so gcTime can be short.
 */
export const CACHE_TIMES = {
  // Default gc time: 5 minutes (IndexedDB handles offline persistence)
  DEFAULT: 5 * 60 * 1000,
  // RA profiles: 10 minutes (heavy but frequently searched)
  RA_PROFILES: 10 * 60 * 1000,
  // Shop data: 15 minutes (frequently used in shop flow)
  SHOP: 15 * 60 * 1000,
  // Standard data: 10 minutes
  STANDARD: 10 * 60 * 1000,
  // Short-lived data: 5 minutes
  SHORT: 5 * 60 * 1000,
  // Stale times
  STALE_SHORT: 5 * 60 * 1000,   // 5 minutes
  STALE_MEDIUM: 15 * 60 * 1000, // 15 minutes
  STALE_LONG: 60 * 60 * 1000,   // 1 hour
} as const;

/**
 * Query Client with persistence-optimized defaults
 * gcTime set high to allow IndexedDB persistence to work
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Default stale time: 5 minutes
      staleTime: CACHE_TIMES.STALE_SHORT,
      // GC time: 30 minutes — must be >> staleTime to enable stale-while-revalidate.
      // IndexedDB persister handles offline; this keeps in-memory cache alive for navigation.
      gcTime: 30 * 60 * 1000,
      // Retry failed queries once
      retry: 1,
      // Retry delay with exponential backoff
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Don't refetch on window focus (reduces unnecessary API calls)
      refetchOnWindowFocus: false,
      // Always refetch when reconnecting (important for offline support)
      refetchOnReconnect: 'always',
      // Enable background updates on mount
      refetchOnMount: true,
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
      // Retry delay for mutations
      retryDelay: 1000,
    },
  },
});

/**
 * Query keys factory for consistency
 * Extended with all data types needed for offline support
 */
export const queryKeys = {
  raProfiles: {
    all: ['ra-profiles'] as const,
    lists: () => [...queryKeys.raProfiles.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.raProfiles.lists(), filters] as const,
    details: () => [...queryKeys.raProfiles.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.raProfiles.details(), id] as const,
    search: (term: string) =>
      [...queryKeys.raProfiles.all, 'search', term] as const,
    stats: () => ['ra-stats'] as const,
    byCfa: () => ['ra-profiles-by-cfa'] as const,
  },
  shop: {
    all: ['shop'] as const,
    dashboard: () => [...queryKeys.shop.all, 'dashboard'] as const,
    products: (filters?: Record<string, unknown>) =>
      ['shop-products', filters ?? {}] as const,
    product: (id: string) => ['shop-product', id] as const,
    stats: () => ['shop-stats'] as const,
    categories: () => ['shop-categories'] as const,
    lowStock: () => ['shop-low-stock'] as const,
    deliveries: (raProfileId?: string) =>
      ['shop-deliveries', raProfileId ?? 'all'] as const,
    returns: (raProfileId?: string) =>
      ['shop-returns', raProfileId ?? 'all'] as const,
  },
  laundry: {
    all: ['laundry'] as const,
    nets: (filters?: Record<string, unknown>) =>
      ['laundry-nets', filters ?? {}] as const,
    net: (id: string) => ['laundry-net', id] as const,
    movements: (netId?: string) =>
      ['laundry-movements', netId ?? 'all'] as const,
    stats: () => ['laundry-stats'] as const,
  },
  cfa: {
    all: ['cfa'] as const,
    list: () => [...queryKeys.cfa.all, 'list'] as const,
  },
} as const;

/**
 * Error handler for query errors
 */
export const queryErrorHandler = (error: Error) => {
  console.error('[React Query] Error:', error.message);
};

/**
 * Get recommended gcTime for a query key
 * Used by hooks to set appropriate cache times
 */
export const getGcTimeForQueryKey = (queryKey: readonly unknown[]): number => {
  if (!queryKey || queryKey.length === 0) return CACHE_TIMES.DEFAULT;

  const firstKey = queryKey[0];
  if (typeof firstKey !== 'string') return CACHE_TIMES.DEFAULT;

  // RA profiles: 10 minutes (heavy decrypted data, frequently searched)
  if (
    firstKey.startsWith('ra-profiles') ||
    firstKey.startsWith('ra-profile') ||
    firstKey.startsWith('ra-list')
  ) {
    return CACHE_TIMES.RA_PROFILES;
  }

  // Shop data: 15 minutes (frequently used in shop flow)
  if (
    firstKey.startsWith('shop-products') ||
    firstKey.startsWith('shop-product') ||
    firstKey.startsWith('shop-deliveries') ||
    firstKey.startsWith('shop-categories')
  ) {
    return CACHE_TIMES.SHOP;
  }

  // Clothing requests: 5 minutes (default)
  // Laundry, stats, and everything else: default 5 minutes
  return CACHE_TIMES.DEFAULT;
};
